import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { company } from '~/company/Company';
import { Button, SIZE, VARIANT } from '~/components/isolated/Button';
import { Counter } from '~/components/isolated/Counter';
import { useCartVibration } from '~/hooks/useCartVibration';
import { useNotifyMe } from '~/hooks/useNotifyMe';
import type { ProductViewProps } from '~/pages/Product/Product';
import { cartStore, mainStore } from '~/stores';
import { formatPriceWithCurrency } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
} from '~/utils/multibuy';

import styles from './AddToCardButton.module.scss';

const AddToCardButton = ({
  handleChangeCount,
  count,
  limit,
  isLoading,
  product,
  currentItem,
  isBundle,
  isMultiBuy,
}: Pick<
  ProductViewProps,
  | 'handleChangeCount'
  | 'count'
  | 'limit'
  | 'isLoading'
  | 'product'
  | 'currentItem'
  | 'isBundle'
  | 'isMultiBuy'
>) => {
  const { t } = useTranslation();

  const lowestLevel = currentItem
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;
  const applicableLevel = currentItem
    ? getApplicableMultibuyLevel(currentItem.multibuyPriceLevels, count)
    : null;
  const multibuyRequiredCount = lowestLevel?.quantity ?? 0;
  const isMultibuyActivated = applicableLevel !== null;

  const { vibrateOnChange } = useCartVibration({
    isMultibuy: isMultiBuy,
    requiredQuantity: multibuyRequiredCount,
    currentCount: count,
  });

  const {
    notifyMeOnClick,
    notifyMeIconV2,
    notifyMeButtonText,
    btnClassName,
    isLoading: isSubscribtionLoading,
  } = useNotifyMe(
    product?.data?.id ?? 0,
    product?.selectedOffer?.sku ?? currentItem?.sku ?? '',
    isBundle,
  );

  const calculatedPrice = (() => {
    if (isMultiBuy && isMultibuyActivated && applicableLevel) {
      const promoPrice = mainStore.convertPenceToPounds(
        applicableLevel.unitPrice * count,
      );
      return formatPriceWithCurrency(promoPrice);
    }

    const promo = currentItem?.mixAndMatch;
    const isMixAndMatchReached =
      !!promo && cartStore.mixAndMatchUnits >= promo.threshold;
    const priceToUse = isMixAndMatchReached
      ? promo.price
      : currentItem?.discountPrice || currentItem?.price || 0;
    return formatPriceWithCurrency(
      mainStore.convertPenceToPounds(priceToUse * count),
    );
  })();

  const handleDecrement = () => {
    const newCount = count - 1 <= 0 ? 0 : count - 1;
    vibrateOnChange(newCount, 'remove');
    handleChangeCount(newCount, 'remove');
  };

  const handleIncrement = () => {
    const newCount = count + 1;
    vibrateOnChange(newCount, 'add');
    handleChangeCount(newCount, 'add');
  };

  if (isLoading) {
    return (
      <div className={styles.root}>
        <Skeleton containerClassName={styles.skeleton} />
      </div>
    );
  }
  return (
    <div className={styles.root}>
      {limit <= 0 && (
        <Button
          size={SIZE.XL}
          variant={VARIANT.shadowS}
          className={classes([styles.notifyButton, btnClassName])}
          onClick={notifyMeOnClick}
          endIcon={notifyMeIconV2}
          disabled={isSubscribtionLoading}
        >
          {notifyMeButtonText}
        </Button>
      )}
      {limit > 0 && (
        <>
          {count < 1 && (
            <Button
              size={SIZE.XL}
              className={styles.button}
              onClick={() => {
                vibrateOnChange(1, 'add');
                handleChangeCount(count + 1, 'add');
              }}
              endIcon={company.match({
                mcosmetic: null,
                default: 'plus-alt',
              })}
            >
              {t('addToCart')}
            </Button>
          )}
          {count > 0 && (
            <Counter
              value={count}
              limit={limit}
              onDecrement={handleDecrement}
              onIncrement={handleIncrement}
              classes={{
                root: styles.counterRoot,
                buttonClassName: styles.counterButton,
                disabledClassName: styles.disabled,
                valueClassName: styles.value,
              }}
              calculatedPrice={
                <span
                  className={classes([
                    styles.calculatedPrice,
                    isMultiBuy && isMultibuyActivated && styles.greenPrice,
                  ])}
                >
                  {calculatedPrice}
                </span>
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default observer(AddToCardButton);
